import React, { useState, useEffect } from 'react';
import { Camera, Mail, Phone, MapPin, Facebook, Instagram, Youtube, ExternalLink, X, Sparkles, Heart, Star } from 'lucide-react';

export default function WebSiteHomePage() {
    const [scrollY, setScrollY] = useState(0);
    const [currentHeroImage, setCurrentHeroImage] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImage, setLightboxImage] = useState('');
    const [isVisible, setIsVisible] = useState({});

    const studioData = {
        name: "Bollywood Studio",
        slug: "bollywood-studio",
        logo: "https://bollywoodstudio.admin.kwikmedia.in/logos/bollywoodstudio.png",
        heroImages: [
            "https://images.unsplash.com/photo-1519741497674-611481863552?w=1920",
            "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=1920",
            "https://images.unsplash.com/photo-1591604466107-ec97de577aff?w=1920",
            "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=1920"
        ],
        galleryImages: [
            { images: "https://images.unsplash.com/photo-1519741497674-611481863552?w=800", title: "Wedding Bliss" },
            { images: "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=800", title: "Candid Moments" },
            { images: "https://images.unsplash.com/photo-1591604466107-ec97de577aff?w=800", title: "Pre-Wedding" },
            { images: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=800", title: "Reception" }
        ],
        socialMedia: {
            facebook: "https://facebook.com/bollywoodstudio",
            instagram: "https://instagram.com/bollywoodstudio",
            youtube: "https://youtube.com/@bollywoodstudio",
            portfolio: "https://portfolio.bollywoodstudio.com"
        },
        services: [
            { title: "Wedding Photography", description: "Capture your special day with stunning, candid shots that tell your unique love story.", icon: "Camera" },
            { title: "Pre-Wedding Shoots", description: "Creative and romantic pre-wedding photography sessions at breathtaking locations.", icon: "Camera" },
            { title: "Cinematic Videography", description: "Professional wedding films with cinematic quality and emotional storytelling.", icon: "Camera" },
            { title: "Drone Photography", description: "Stunning aerial shots and videos to add a unique perspective to your memories.", icon: "Camera" }
        ],
        sections: [
            {
                id: "wedding",
                heading: "Wedding",
                background: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200",
                samples: [
                    "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=400",
                    "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=400",
                    "https://images.unsplash.com/photo-1519741497674-611481863552?w=400",
                    "https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=400"
                ]
            },
            {
                id: "prewedding",
                heading: "Pre-Wedding",
                background: "https://images.unsplash.com/photo-1591604466107-ec97de577aff?w=1200",
                samples: [
                    "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=400",
                    "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=400",
                    "https://images.unsplash.com/photo-1623690498301-472e6cf72ea8?w=400",
                    "https://images.unsplash.com/photo-1602173574767-37ac01994b2a?w=400"
                ]
            },
            {
                id: "candid",
                heading: "Candid Moments",
                background: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=1200",
                samples: [
                    "https://images.unsplash.com/photo-1525258116429-c1e8bb1e7aeb?w=400",
                    "https://images.unsplash.com/photo-1519741497674-611481863552?w=400",
                    "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=400",
                    "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=400"
                ]
            }
        ],
        contact: {
            phone: "+91 98765 43210",
            email: "contact@bollywoodstudio.com",
            address: "123 Photography Lane, Ahmedabad, Gujarat 380001"
        }
    };

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentHeroImage((prev) => (prev + 1) % studioData.heroImages.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    setIsVisible(prev => ({
                        ...prev,
                        [entry.target.id]: entry.isIntersecting
                    }));
                });
            },
            { threshold: 0.1 }
        );

        document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const openLightbox = (image) => {
        setLightboxImage(image);
        setLightboxOpen(true);
    };

    return (
        <div className="bg-white text-gray-900 overflow-x-hidden">
            {/* Floating Elements Background */}
            <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
                {[...Array(15)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute animate-float opacity-20"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 5}s`,
                            animationDuration: `${10 + Math.random() * 10}s`
                        }}
                    >
                        {i % 3 === 0 ? (
                            <Heart className="w-6 h-6 text-pink-400" />
                        ) : i % 3 === 1 ? (
                            <Star className="w-4 h-4 text-yellow-400" />
                        ) : (
                            <Sparkles className="w-5 h-5 text-orange-400" />
                        )}
                    </div>
                ))}
            </div>

            {/* Hero Section with Slideshow */}
            <section className="relative h-screen flex items-center justify-center overflow-hidden">
                {studioData.heroImages.map((img, index) => (
                    <div
                        key={index}
                        className={`absolute inset-0 bg-cover bg-center transition-all duration-2000 ${index === currentHeroImage ? 'opacity-100 scale-100' : 'opacity-0 scale-110'
                            }`}
                        style={{
                            backgroundImage: `url(${img})`,
                            transform: index === currentHeroImage
                                ? `translateY(${scrollY * 0.5}px) scale(${1 + scrollY * 0.0002})`
                                : 'scale(1.1)',
                            filter: 'brightness(0.5)'
                        }}
                    />
                ))}

                <div className="absolute inset-0 animate-gradient-shift" />

                {/* Animated Geometric Shapes */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full animate-pulse-slow opacity-10" style={{
                        background: 'linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%)'
                    }} />
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full animate-pulse-slower opacity-10" style={{
                        background: 'linear-gradient(144.01deg, #C94DAA 0%, #FFC951 100%)'
                    }} />
                </div>

                <div className="relative z-10 text-center px-4">
                    <div className="mb-6 animate-logo-entrance">
                        <img src={studioData.logo} alt="Logo" className="w-[250px] h-[150px] mx-auto drop-shadow-2xl animate-logo-pulse" />
                    </div>
                    <h1 className="text-6xl md:text-8xl font-bold mb-4 text-white drop-shadow-lg animate-title-entrance">
                        {studioData.name}
                    </h1>
                    <p className="text-xl md:text-2xl text-white mb-8 drop-shadow animate-subtitle-entrance">
                        Capturing Memories, Creating Masterpieces
                    </p>
                    <div className="flex gap-4 justify-center animate-buttons-entrance">
                        <a href="#gallery" className="px-8 py-3 rounded-full font-semibold text-white shadow-2xl transform hover:scale-110 hover:shadow-pink-500/50 transition-all duration-500 animate-shimmer" style={{
                            background: 'linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%)'
                        }}>
                            View Portfolio
                        </a>
                        <a href="#contact" className="border-2 border-white bg-white/20 backdrop-blur-sm px-8 py-3 rounded-full font-semibold text-white hover:bg-white hover:text-gray-900 transform hover:scale-110 transition-all duration-500">
                            Get in Touch
                        </a>
                    </div>
                </div>

                {/* Slideshow Indicators */}
                <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
                    {studioData.heroImages.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentHeroImage(index)}
                            className={`w-2 h-2 rounded-full transition-all duration-500 ${index === currentHeroImage ? 'w-8 opacity-100' : 'opacity-50'
                                }`}
                            style={{
                                background: 'linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%)'
                            }}
                        />
                    ))}
                </div>

                {/* <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce z-20">
                    <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
                        <div className="w-1 h-3 rounded-full mt-2 animate-scroll" style={{
                            background: 'linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%)'
                        }} />
                    </div>
                </div> */}
            </section>

            {/* Services Section */}
            <section id="services" className="py-20 px-4 bg-gradient-to-b from-orange-50 to-pink-50 relative">
                <div className="max-w-7xl mx-auto">
                    <h2
                        id="services-title"
                        data-animate
                        className={`text-5xl font-bold text-center mb-16 transition-all duration-1000 ${isVisible['services-title'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                            }`}
                    >
                        Our <span className="bg-clip-text text-transparent animate-text-gradient" style={{
                            backgroundImage: 'linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%)'
                        }}>Services</span>
                    </h2>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {studioData.services.map((service, index) => (
                            <div
                                key={index}
                                id={`service-${index}`}
                                data-animate
                                className={`bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-4 hover:rotate-1 transition-all duration-500 border border-orange-100 group ${isVisible[`service-${index}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                                    }`}
                                style={{ transitionDelay: `${index * 150}ms` }}
                            >
                                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-500 shadow-md animate-icon-float" style={{
                                    background: 'linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%)'
                                }}>
                                    <Camera className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-bold mb-3 text-gray-800">{service.title}</h3>
                                <p className="text-gray-600">{service.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Gallery Grid */}
            <section id="gallery" className="py-20 px-4 bg-white relative">
                <div className="max-w-7xl mx-auto">
                    <h2
                        id="gallery-title"
                        data-animate
                        className={`text-5xl font-bold text-center mb-16 transition-all duration-1000 ${isVisible['gallery-title'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                            }`}
                    >
                        Featured <span className="bg-clip-text text-transparent" style={{
                            backgroundImage: 'linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%)'
                        }}>Gallery</span>
                    </h2>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {studioData.galleryImages.map((img, index) => (
                            <div
                                key={index}
                                id={`gallery-${index}`}
                                data-animate
                                className={`relative group cursor-pointer overflow-hidden rounded-2xl aspect-square shadow-lg hover:shadow-2xl transition-all duration-700 ${isVisible[`gallery-${index}`] ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-95 rotate-3'
                                    }`}
                                style={{ transitionDelay: `${index * 150}ms` }}
                                onClick={() => openLightbox(img.images)}
                            >
                                <img
                                    src={img.images}
                                    alt={img.title}
                                    className="w-full h-full object-cover transform group-hover:scale-125 group-hover:rotate-3 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{
                                    background: 'linear-gradient(to top, rgba(255, 201, 81, 0.9), rgba(201, 77, 170, 0.7))'
                                }}>
                                    <div className="absolute bottom-4 left-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                                        <h3 className="text-xl font-bold text-white drop-shadow">{img.title}</h3>
                                    </div>
                                    <div className="absolute top-4 right-4 transform -translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                                        <Sparkles className="w-6 h-6 text-white animate-spin-slow" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Portfolio Sections */}
            {studioData.sections.map((section, sectionIndex) => (
                <section
                    key={section.id}
                    id={section.id}
                    className="relative py-20 px-4"
                    style={{
                        background: sectionIndex % 2 === 0
                            ? 'linear-gradient(to bottom, #FFF5E6, #FFE9F5)'
                            : 'linear-gradient(to bottom, #FFE9F5, #FFF5E6)'
                    }}
                >
                    <div className="max-w-7xl mx-auto">
                        <div
                            id={`section-${section.id}`}
                            data-animate
                            className={`text-center mb-12 transition-all duration-1000 ${isVisible[`section-${section.id}`] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                                }`}
                        >
                            <h2 className="text-5xl font-bold mb-4 text-gray-800">{section.heading}</h2>
                            <div className="w-24 h-1 mx-auto rounded-full animate-pulse-slow" style={{
                                background: 'linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%)'
                            }} />
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {section.samples.map((sample, index) => (
                                <div
                                    key={index}
                                    id={`${section.id}-sample-${index}`}
                                    data-animate
                                    className={`relative group cursor-pointer overflow-hidden rounded-2xl aspect-[3/4] shadow-lg hover:shadow-2xl transition-all duration-700 ${isVisible[`${section.id}-sample-${index}`] ? 'opacity-100 translate-x-0 rotate-0' : 'opacity-0 -translate-x-10 -rotate-3'
                                        }`}
                                    style={{ transitionDelay: `${index * 150}ms` }}
                                    onClick={() => openLightbox(sample)}
                                >
                                    <img
                                        src={sample}
                                        alt={`${section.heading} ${index + 1}`}
                                        className="w-full h-full object-cover transform group-hover:scale-110 group-hover:rotate-2 transition-all duration-700"
                                    />
                                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{
                                        background: 'linear-gradient(to top, rgba(255, 201, 81, 0.7), transparent)'
                                    }}>
                                        <div className="absolute bottom-4 right-4">
                                            <Camera className="w-8 h-8 text-white animate-bounce" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            ))}

            {/* Contact Section */}
            <section id="contact" className="py-20 px-4 bg-gradient-to-b from-pink-50 to-orange-50 relative">
                <div className="max-w-4xl mx-auto">
                    <h2
                        id="contact-title"
                        data-animate
                        className={`text-5xl font-bold text-center mb-16 transition-all duration-1000 ${isVisible['contact-title'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                            }`}
                    >
                        Get in <span className="bg-clip-text text-transparent" style={{
                            backgroundImage: 'linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%)'
                        }}>Touch</span>
                    </h2>

                    <div className="grid md:grid-cols-2 gap-8 mb-12">
                        <div
                            id="contact-info"
                            data-animate
                            className={`space-y-6 transition-all duration-1000 ${isVisible['contact-info'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                                }`}
                        >
                            <div className="flex items-start gap-4 group cursor-pointer hover:translate-x-2 transition-transform duration-300">
                                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-md animate-icon-float" style={{
                                    background: 'linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%)'
                                }}>
                                    <Phone className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-lg mb-1 text-gray-800">Phone</h3>
                                    <p className="text-gray-600">{studioData.contact.phone}</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4 group cursor-pointer hover:translate-x-2 transition-transform duration-300">
                                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-md animate-icon-float-delayed" style={{
                                    background: 'linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%)'
                                }}>
                                    <Mail className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-lg mb-1 text-gray-800">Email</h3>
                                    <p className="text-gray-600">{studioData.contact.email}</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4 group cursor-pointer hover:translate-x-2 transition-transform duration-300">
                                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-md animate-icon-float-more-delayed" style={{
                                    background: 'linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%)'
                                }}>
                                    <MapPin className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-lg mb-1 text-gray-800">Address</h3>
                                    <p className="text-gray-600">{studioData.contact.address}</p>
                                </div>
                            </div>
                        </div>

                        <div
                            id="contact-form"
                            data-animate
                            className={`bg-white p-6 rounded-2xl shadow-lg transition-all duration-1000 border border-orange-100 ${isVisible['contact-form'] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                                }`}
                        >
                            <input
                                type="text"
                                placeholder="Your Name"
                                className="w-full bg-orange-50 px-4 py-3 rounded-lg mb-4 focus:outline-none focus:ring-2 border border-orange-200 text-gray-800 transition-all duration-300 focus:scale-105"
                            />
                            <input
                                type="email"
                                placeholder="Your Email"
                                className="w-full bg-orange-50 px-4 py-3 rounded-lg mb-4 focus:outline-none focus:ring-2 border border-orange-200 text-gray-800 transition-all duration-300 focus:scale-105"
                            />
                            <textarea
                                placeholder="Your Message"
                                rows="4"
                                className="w-full bg-orange-50 px-4 py-3 rounded-lg mb-4 focus:outline-none focus:ring-2 border border-orange-200 text-gray-800 transition-all duration-300 focus:scale-105"
                            />
                            <button className="w-full py-3 rounded-lg font-semibold text-white shadow-lg transform hover:scale-105 hover:shadow-2xl transition-all duration-300 animate-shimmer" style={{
                                background: 'linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%)'
                            }}>
                                Send Message
                            </button>
                        </div>
                    </div>

                    {/* Social Media */}
                    <div className="flex justify-center gap-6">
                        {studioData.socialMedia.facebook && (
                            <a href={studioData.socialMedia.facebook} className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md transform hover:scale-125 hover:rotate-12 transition-all duration-300 border border-orange-200 text-gray-700 group">
                                <Facebook className="w-6 h-6 group-hover:text-pink-500 transition-colors" />
                            </a>
                        )}
                        {studioData.socialMedia.instagram && (
                            <a href={studioData.socialMedia.instagram} className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md transform hover:scale-125 hover:rotate-12 transition-all duration-300 border border-orange-200 text-gray-700 group">
                                <Instagram className="w-6 h-6 group-hover:text-pink-500 transition-colors" />
                            </a>
                        )}
                        {studioData.socialMedia.youtube && (
                            <a href={studioData.socialMedia.youtube} className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md transform hover:scale-125 hover:rotate-12 transition-all duration-300 border border-orange-200 text-gray-700 group">
                                <Youtube className="w-6 h-6 group-hover:text-pink-500 transition-colors" />
                            </a>
                        )}
                        {studioData.socialMedia.portfolio && (
                            <a href={studioData.socialMedia.portfolio} className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md transform hover:scale-125 hover:rotate-12 transition-all duration-300 border border-orange-200 text-gray-700 group">
                                <ExternalLink className="w-6 h-6 group-hover:text-pink-500 transition-colors" />
                            </a>
                        )}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 text-center text-gray-600 border-t border-orange-200" style={{
                background: 'linear-gradient(144.01deg, #FFF5E6 0%, #FFE9F5 100%)'
            }}>
                <p>© 2026 {studioData.name}. All rights reserved.</p>
            </footer>

            {/* Lightbox */}
            {lightboxOpen && (
                <div
                    className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 animate-fade-in"
                    onClick={() => setLightboxOpen(false)}
                >
                    <button
                        className="absolute top-4 right-4 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300 text-gray-800 shadow-lg"
                        onClick={() => setLightboxOpen(false)}
                    >
                        <X className="w-6 h-6" />
                    </button>
                    <img
                        src={lightboxImage}
                        alt="Lightbox"
                        className="max-w-full max-h-full object-contain animate-scale-in rounded-lg shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}

            <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-20px) translateX(10px); }
          50% { transform: translateY(-10px) translateX(-10px); }
          75% { transform: translateY(-30px) translateX(5px); }
        }

        @keyframes gradient-shift {
          0%, 100% { background: linear-gradient(to bottom, rgba(255, 201, 81, 0.1), rgba(201, 77, 170, 0.1)); }
          50% { background: linear-gradient(to bottom,rgba(255, 201, 81, 0.1), rgba(201, 77, 170, 0.1)); }
        }

        @keyframes pulse-slow {
          0%, 100% { transform: scale(1); opacity: 0.1; }
          50% { transform: scale(1.1); opacity: 0.15; }
        }

        @keyframes pulse-slower {
          0%, 100% { transform: scale(1); opacity: 0.1; }
          50% { transform: scale(1.15); opacity: 0.15; }
        }

        @keyframes logo-entrance {
          from {
            opacity: 0;
            transform: scale(0) rotate(-180deg);
          }
          to {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }

        @keyframes logo-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        @keyframes title-entrance {
          from {
            opacity: 0;
            transform: translateY(50px);
            letter-spacing: 20px;
          }
          to {
            opacity: 1;
            transform: translateY(0);
            letter-spacing: normal;
          }
        }

        @keyframes subtitle-entrance {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes buttons-entrance {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }

        @keyframes icon-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes icon-float-delayed {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes icon-float-more-delayed {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes scroll {
          0% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(20px); opacity: 0; }
        }

        .animate-float {
          animation: float 15s ease-in-out infinite;
        }

        .animate-gradient-shift {
          animation: gradient-shift 10s ease-in-out infinite;
        }

        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }

        .animate-pulse-slower {
          animation: pulse-slower 6s ease-in-out infinite;
        }

        .animate-logo-entrance {
          animation: logo-entrance 1s ease-out;
        }

        .animate-logo-pulse {
          animation: logo-pulse 3s ease-in-out infinite;
        }

        .animate-title-entrance {
          animation: title-entrance 1.2s ease-out 0.3s both;
        }

        .animate-subtitle-entrance {
          animation: subtitle-entrance 1s ease-out 0.6s both;
        }

        .animate-buttons-entrance {
          animation: buttons-entrance 1s ease-out 0.9s both;
        }

        .animate-shimmer {
          background-size: 200% 100%;
          animation: shimmer 3s linear infinite;
        }

        .animate-icon-float {
          animation: icon-float 3s ease-in-out infinite;
        }

        .animate-icon-float-delayed {
          animation: icon-float-delayed 3s ease-in-out 0.2s infinite;
        }

        .animate-icon-float-more-delayed {
          animation: icon-float-more-delayed 3s ease-in-out 0.4s infinite;
        }

        .animate-spin-slow {
          animation: spin-slow 4s linear infinite;
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }

        .animate-scroll {
          animation: scroll 1.5s ease-in-out infinite;
        }

        .transition-all {
          transition-property: all;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        }

        .duration-2000 {
          transition-duration: 2000ms;
        }
      `}</style>
        </div>
    );
}